/**
 * @/lib/api.ts
 * LINA API Client - Aligned with ARCHITECTURE.md v2.0
 *
 * This module provides the frontend interface to LINA's backend services.
 *
 * Backend Endpoints (as per ARCHITECTURE.md):
 * - v3-interpret: Main message analysis with single LLM call
 * - v2-feedback: User feedback collection
 * - v2-ux-event: UX event tracking
 *
 * @see /ARCHITECTURE.md for complete system documentation
 */

import { supabase } from './supabase'
import { invokeWithAuth } from './api/client'
import { normalizeThreadFetchResponse } from './api/analysisAdapter'
import type { EvidenceItem } from '@/utils/evidence-matcher'
export { invokeWithAuth } from './api/client'

/** Narrow unknown invoke payloads that carry an error string. */
function readApiError(data: unknown): string | null {
  if (!data || typeof data !== 'object') return null
  const error = (data as { error?: unknown }).error
  return typeof error === 'string' && error.trim() ? error : null
}

// ===================================
// Response Type Interfaces
// ===================================

/**
 * Response from v3-interpret endpoint
 * @see ARCHITECTURE.md - "API Endpoints" > "v3/interpret" section
 *
 * v3.0 Backend Response Format:
 * {
 *   analysis_event_id: string,
 *   tone: string,              // e.g., "friendly", "hostile", "sarcastic"
 *   nuance: string,            // e.g., "curious", "anxious", "angry"
 *   intention: string,         // e.g., "checking_in", "seeking_help"
 *   tone_evidence: [[start, end], ...],
 *   nuance_evidence: [[start, end], ...],
 *   intention_evidence: [[start, end], ...],
 *   summary: string,
 *   recommendation: string,
 *   warning: string | null,
 *   potential_responses: {
 *     "Label1": { message: string, explanation: string },
 *     "Label2": { message: string, explanation: string },
 *     "Label3": { message: string, explanation: string }
 *   },
 *   // Safety assessment (all fields from safety.ts)
 *   safety_risk: 0|1|2|3,
 *   category_scores: {...},
 *   response_guidance: {...}
 * }
 *
 * IMPORTANT: v2.0 returns 3 analysis categories (tone, nuance, intention) plus
 * comprehensive safety assessment in a single LLM call.
 */
export interface V2InterpretResponse {
  analysis_event_id: string
  session_id: string // Session ID linking analysis → responses → usage
  tone: string
  nuance: string
  intention: string
  tone_evidence: Array<[number, number]>
  nuance_evidence: Array<[number, number]>
  intention_evidence: Array<[number, number]>
  tone_evidence_explanation?: string
  nuance_evidence_explanation?: string
  intention_evidence_explanation?: string
  summary: string
  warning: string | null
  recommendation: string
  potential_responses: Record<string, { message: string; explanation: string }>
  // Safety assessment (comprehensive)
  safety_risk?: 0 | 1 | 2 | 3
  safety_types?: string[]
  safety_evidence?: Array<{
    span: [number, number]
    type: string
    severity: 0 | 1 | 2 | 3
  }>
  safety_label?: string
  safety_confidence?: number
  category_scores?: {
    violence_threat: 0 | 1 | 2 | 3
    self_harm: 0 | 1 | 2 | 3
    sexual_exploitation: 0 | 1 | 2 | 3
    financial_scam: 0 | 1 | 2 | 3
    coercion_manipulation: 0 | 1 | 2 | 3
    harassment_bullying: 0 | 1 | 2 | 3
    privacy_risk: 0 | 1 | 2 | 3
  }
  primary_category?: string
  indicators?: string[]
  safe_to_respond?: boolean
  response_guidance?: {
    should_respond: boolean
    response_type: 'none' | 'boundary' | 'refusal' | 'neutral' | 'friendly'
    reasoning: string
    suggested_actions: string[]
  }
  // Error fields for token exhaustion
  error?: string
  code?: string
}

/**
 * Request format for v3-interpret endpoint
 * @see ARCHITECTURE.md - "API Endpoints" > "v3/interpret" section
 */
export interface V2InterpretRequest {
  message: string
  sender_type: string
  context?: string | null
  response_tone?: 'friendly' | 'direct' | 'thoughtful'
  demo_mode?: boolean // When true, skip token checks and consumption (for onboarding demos)
  birthdate?: string | null
  reading_level?: string | null
}

/**
 * Feedback options interface
 * @see ARCHITECTURE.md - "API Endpoints" > "v2/feedback" section
 *
 * UX event tracking options
 * @see ARCHITECTURE.md - "API Endpoints" > "v2/ux-event" section
 */
export interface UXEventOptions {
  event_type: string
  analysis_event_id?: string
  metadata?: Record<string, any>
}

// ===================================
// Main API: v3-interpret
// ===================================

/**
 * Call v3-interpret endpoint to analyze a message
 *
 * This is the ONLY analysis endpoint in v2.0 architecture.
 * It performs a single LLM call that handles:
 * - Tone, Nuance, Intention analysis with evidence
 * - Safety assessment (risk levels, category scores, response guidance)
 * - Summary generation
 * - Warning detection
 * - Recommendation
 * - 3 Potential responses
 *
 * @see ARCHITECTURE.md - "Message Analysis Flow" section
 * @param request - Message analysis request
 * @returns Promise<V2InterpretResponse> - Analysis results
 */
export async function analyzeMessage(request: V2InterpretRequest): Promise<V2InterpretResponse> {
  console.log('[v3-interpret] Starting analysis...')
  console.log(
    '[v3-interpret] 🔍 DIAGNOSTIC: sender_type in request:',
    JSON.stringify({ sender_type: request.sender_type, full_request: request }),
  )

  const { data, error } = await invokeWithAuth('v3-interpret', {
    body: {
      message: request.message,
      sender_type: request.sender_type,
      context: request.context || null,
      response_tone: request.response_tone,
      demo_mode: request.demo_mode || false,
    },
  })

  if (error) {
    console.error('[v3-interpret] API Error:', error)
    throw new Error(`API Error: ${error.message}`)
  }

  const payloadError = readApiError(data)
  if (payloadError) {
    console.error('[v3-interpret] Backend returned error:', data)
    throw new Error(payloadError)
  }

  console.log('[v3-interpret] Analysis complete')
  return data as V2InterpretResponse
}

// ===================================
// Feedback & UX Tracking API
// ===================================

/**
 * Track UX events (copy, view, click, etc.)
 * @see ARCHITECTURE.md - "API Endpoints" > "v2/ux-event" section
 *
 * Tracks user interactions for UX analytics and improvement.
 * Events: 'response_copied', 'response_sent', 'badge_clicked', 'help_viewed'
 */
export async function trackUXEvent(options: UXEventOptions): Promise<void> {
  try {
    const { error } = await invokeWithAuth('v2-ux-event', {
      body: {
        event_type: options.event_type,
        analysis_event_id: options.analysis_event_id,
        metadata: options.metadata || {},
      },
      quietFetchFailure: true,
    })

    if (error) {
      console.warn(`[v2-ux-event] UX event tracking failed: ${error.message}`)
      // Don't throw - UX tracking should not break user flow
    } else if (import.meta.env.DEV) {
      console.log(`[v2-ux-event] Tracked ${options.event_type}`)
    }
  } catch (err) {
    console.warn('[v2-ux-event] UX event tracking failed:', err)
  }
}

// ===================================
// Analysis Feedback
// ===================================

export interface SubmitAnalysisFeedbackRequest {
  analysisEventId?: string
  sessionId?: string
  feedbackType: 'positive' | 'negative'
  feedbackText?: string
}

/**
 * Submit feedback on analysis quality (thumbs up/down)
 * Stores feedback for learning and improvement purposes
 */
export async function submitAnalysisFeedback(
  request: SubmitAnalysisFeedbackRequest,
): Promise<void> {
  try {
    await invokeWithAuth<{ success: boolean; feedback: any }>('submit-analysis-feedback', {
      method: 'POST',
      body: request,
    })

    console.log('[submitAnalysisFeedback] Feedback submitted successfully')
    return
  } catch (error) {
    console.error('[submitAnalysisFeedback] Error submitting feedback:', error)
    throw error
  }
}

/**
 * Update existing feedback with additional text
 * Used when user adds feedback text after initial thumbs down
 */
export async function updateAnalysisFeedback(
  analysisEventId: string,
  feedbackText: string,
): Promise<void> {
  try {
    await invokeWithAuth<{ success: boolean; feedback: any }>('submit-analysis-feedback', {
      method: 'PATCH',
      body: { analysisEventId, feedbackText },
    })

    console.log('[updateAnalysisFeedback] Feedback updated successfully')
    return
  } catch (error) {
    console.error('[updateAnalysisFeedback] Error updating feedback:', error)
    throw error
  }
}

// ===================================
// Response Style Preferences
// ===================================
// Health Check
// ===================================

/**
 * Check if the API is responsive
 * @returns Promise with health status
 */
export async function checkAPIHealth(): Promise<{
  status: 'healthy' | 'down'
  timestamp: string
}> {
  try {
    const { data, error } = await invokeWithAuth('v5-interpret', {
      body: {
        message: 'Health check',
        sender_type: 'system',
        context: null,
      },
    })

    if (error || readApiError(data)) {
      throw new Error('Health check failed')
    }

    return {
      status: 'healthy',
      timestamp: new Date().toISOString(),
    }
  } catch (error) {
    console.error('[health-check] API health check failed:', error)
    return {
      status: 'down',
      timestamp: new Date().toISOString(),
    }
  }
}

// ===================================
// Thread Analysis (Conversation Thread)
// ===================================

export interface ThreadFetchRequest {
  url: string
}

export interface ThreadComment {
  author: string
  text: string
  depth: number
}

export interface ThreadFetchResponse {
  platform: string
  post_text: string
  post_author: string
  comments: ThreadComment[]
}

export interface ThreadAnalyzeRequest {
  thread_url: string
  platform: string
  post_text: string
  post_author: string
  comments: ThreadComment[]
  additional_context?: string
}

export interface ThreadResponseGuidance {
  considerations: string[]
  discussion_angles: string[]
  tone_advice: string
}

export type SocialRiskLevelLabel = 'green' | 'yellow' | 'orange' | 'red'

export interface SocialRiskScores {
  conflict: number
  toxicity: number
  pile_on: number
  tone_contamination: number
  diversity: number
}

export interface ThreadAnalyzeResponse {
  risk_level: 0 | 1 | 2 | 3
  conversation_temperature: 'calm' | 'debate' | 'heated' | 'hostile'
  summary: string
  conversation_tone: string
  hidden_dynamics: string
  post_intent: string
  coaching_explanation: string
  response_guidance: ThreadResponseGuidance
  /** Present when analysis used 5-dimension social risk (server-computed from scores) */
  risk_level_label?: SocialRiskLevelLabel
  confidence?: number
  scores?: SocialRiskScores
  primary_risk?: string
  participation_guidance?: string
}

/**
 * Fetch post and comments from a thread URL (first 100 comments, flattened).
 */
export async function fetchThread(request: ThreadFetchRequest): Promise<ThreadFetchResponse> {
  const { data, error } = await invokeWithAuth<ThreadFetchResponse>('thread-fetch', {
    body: { url: request.url },
  })
  if (error) throw new Error(error.message || 'Failed to fetch thread')
  const fetchError = readApiError(data)
  if (fetchError) throw new Error(fetchError)
  return data as ThreadFetchResponse
}

/**
 * Analyze a thread and return risk, perspective, coaching, and response guidance.
 * Does not draft a full comment; provides coaching only.
 */
export async function analyzeThread(request: ThreadAnalyzeRequest): Promise<ThreadAnalyzeResponse> {
  const { data, error } = await invokeWithAuth<ThreadAnalyzeResponse>('thread-analyze', {
    body: {
      thread_url: request.thread_url,
      platform: request.platform,
      post_text: request.post_text,
      post_author: request.post_author,
      comments: request.comments,
      additional_context: request.additional_context || undefined,
    },
  })
  if (error) throw new Error(error.message || 'Failed to analyze thread')
  const analyzeError = readApiError(data)
  if (analyzeError) throw new Error(analyzeError)
  return data as ThreadAnalyzeResponse
}

/**
 * Account type for unified account creation
 * Re-exported from accountTypes.ts for backward compatibility
 */
export type { AccountType } from './account/accountTypes'

// Admin functions and types previously re-exported here have been removed.
// Import them directly from `@admin/lib/adminApi`. No app/src consumer
// referenced these re-exports as of the 2026-06-28 knip audit.

// ====================================================================
// Support Chat API
// ====================================================================

import type {
  SupportChatRequest,
  SupportChatResponse,
  SupportSession,
  SupportMessage,
  SupportFeedback,
  SupportEscalation,
} from '@/types/support'

/**
 * Send a message to the support chatbot
 */
export async function sendSupportMessage(
  request: SupportChatRequest,
): Promise<SupportChatResponse> {
  const { data, error } = await invokeWithAuth<SupportChatResponse>('support-chat', {
    method: 'POST',
    body: request,
  })

  if (error) {
    throw new Error(`Failed to send support message: ${error.message}`)
  }

  if (!data || !data.success) {
    throw new Error('Invalid response from support chat')
  }

  return data
}

/**
 * Get support session history
 */
export async function getSupportSession(sessionId: string): Promise<{
  session: SupportSession
  messages: SupportMessage[]
}> {
  const { data: session, error: sessionError } = await supabase
    .from('support_sessions')
    .select('*')
    .eq('id', sessionId)
    .single()

  if (sessionError) {
    throw new Error(`Failed to fetch session: ${sessionError.message}`)
  }

  const { data: messages, error: messagesError } = await supabase
    .from('support_messages')
    .select('*')
    .eq('session_id', sessionId)
    .order('created_at', { ascending: true })

  if (messagesError) {
    throw new Error(`Failed to fetch messages: ${messagesError.message}`)
  }

  return { session, messages: messages || [] }
}

/**
 * Get all support sessions for current user
 */
export async function getSupportSessions(): Promise<SupportSession[]> {
  const { data, error } = await supabase
    .from('support_sessions')
    .select('*')
    .order('last_activity_at', { ascending: false })
    .limit(20)

  if (error) {
    throw new Error(`Failed to fetch sessions: ${error.message}`)
  }

  return data || []
}

/**
 * Submit feedback on a support message
 */
export async function submitSupportFeedback(
  messageId: string,
  rating: -1 | 1,
  comment?: string,
): Promise<SupportFeedback> {
  // Get message to extract session_id and other metadata
  const { data: message, error: messageError } = await supabase
    .from('support_messages')
    .select('session_id, metadata')
    .eq('id', messageId)
    .single()

  if (messageError) {
    throw new Error(`Failed to fetch message: ${messageError.message}`)
  }

  // Get current user's account
  const {
    data: { user },
  } = await supabase.auth.getUser()
  if (!user) throw new Error('Not authenticated')

  const { data: account } = await supabase
    .from('accounts')
    .select('id')
    .eq('user_id', user.id)
    .single()

  if (!account) throw new Error('Account not found')

  const { data: feedback, error: feedbackError } = await supabase
    .from('support_feedback')
    .insert({
      message_id: messageId,
      session_id: message.session_id,
      account_id: account.id,
      rating,
      comment,
      message_intent: message.metadata?.intent,
      message_confidence: message.metadata?.confidence,
    })
    .select()
    .single()

  if (feedbackError) {
    throw new Error(`Failed to submit feedback: ${feedbackError.message}`)
  }

  return feedback
}

/**
 * End/resolve a support session
 */
export async function endSupportSession(sessionId: string): Promise<void> {
  const { error } = await supabase
    .from('support_sessions')
    .update({
      status: 'resolved',
      ended_at: new Date().toISOString(),
    })
    .eq('id', sessionId)

  if (error) {
    throw new Error(`Failed to end session: ${error.message}`)
  }
}

/**
 * Create a support escalation/ticket
 */
export async function createSupportEscalation(data: {
  session_id: string
  issue_summary: string
  priority?: 'low' | 'medium' | 'high' | 'urgent'
  additional_context?: string
  context?: {
    current_page?: string
    feature?: string
    error_state?: {
      error_code?: string
      error_message?: string
      timestamp?: string
    }
    last_action?: string
    analysis_event_id?: string
    auth_token?: string
    browser_info?: string
  }
}): Promise<{ id: string; status: string }> {
  const { data: result, error } = await invokeWithAuth<{ id: string; status: string }>(
    'create-support-ticket',
    {
      method: 'POST',
      body: data,
    },
  )

  if (error) {
    throw new Error(`Failed to create support ticket: ${error.message}`)
  }

  if (!result) {
    throw new Error('Invalid response from ticket creation')
  }

  return result
}

/**
 * Get all support escalations for current user
 */
export async function getUserEscalations(): Promise<SupportEscalation[]> {
  // Get current user's account
  const {
    data: { user },
  } = await supabase.auth.getUser()
  if (!user) throw new Error('Not authenticated')

  const { data: account } = await supabase
    .from('accounts')
    .select('id')
    .eq('user_id', user.id)
    .single()

  if (!account) throw new Error('Account not found')

  const { data, error } = await supabase
    .from('support_escalations')
    .select('*')
    .eq('account_id', account.id)
    .order('created_at', { ascending: false })

  if (error) {
    throw new Error(`Failed to fetch escalations: ${error.message}`)
  }

  return data || []
}

// Conversation Starter API - Used to start a new conversation

export interface ConversationStarterRequest {
  recipient: string
  message_format: string
  user_goal: string
  additional_context?: string
}

export interface ConversationStarterResponse {
  success: boolean
  session_id: string
  message: string
  drafts: string[]
  intention: string
}

export async function startConversation(
  data: ConversationStarterRequest,
): Promise<ConversationStarterResponse> {
  const { data: responseData, error } = await invokeWithAuth<ConversationStarterResponse>(
    'v1-conversation-starter',
    {
      method: 'POST',
      body: data,
    },
  )

  if (error) {
    console.error('[startConversation] API Error:', error)
    throw new Error(`API Error: ${error.message}`)
  }

  if (!responseData) {
    throw new Error('No response data received')
  }

  return responseData
}

// For all edits, continue using the existing v3-response function
// (it already handles refine_draft mode)

// ============================================================================
// Social Comment API (Reddit thread analysis + comment drafting)
// ============================================================================

import type {
  SocialCommentRequest,
  SocialCommentAnalysisResult,
  ThreadData,
} from '@/types/social-comment'

/**
 * Fetch Reddit thread structure without full AI analysis
 * Step 1 of two-step pattern: get thread data for local risk preview
 *
 * @param url - Reddit thread URL
 * @returns Thread data (post, comments, metadata)
 */
export async function fetchRedditThread(url: string): Promise<ThreadData> {
  const { data, error } = await invokeWithAuth<{
    success?: boolean
    thread_data?: ThreadData
    platform?: string
    post_text?: string
    post_author?: string
    comments?: Array<{ author: string; text: string; depth?: number }>
  }>('thread-fetch', {
    method: 'POST',
    body: { url },
  })

  if (error) {
    console.error('[fetchRedditThread] API Error:', error)
    throw new Error(error.message || 'Failed to fetch thread')
  }

  return normalizeThreadFetchResponse(url, data)
}

/**
 * Build request for analyze-message-in-context from ThreadData.
 * target_message = target_comment.body when replying to a comment, else post body.
 */
export function buildAnalyzeMessageInContextRequest(
  threadData: ThreadData,
  threadUrl: string,
  options?: { author_type?: 'user' | 'other'; user_intent?: string; additional_context?: string },
): {
  thread_url: string
  platform: string
  post_text: string
  visible_comments: string[]
  target_message: string
  author_type: 'user' | 'other'
  user_intent?: string
  additional_context?: string
} {
  const postBody = threadData.post?.body ?? threadData.post?.title ?? ''
  const visibleComments = (threadData.top_comments ?? []).map((c) => c.body)
  const targetMessage =
    threadData.target_type === 'comment' && threadData.target_comment?.body
      ? threadData.target_comment.body
      : postBody
  return {
    thread_url: threadUrl,
    platform: 'reddit',
    post_text: postBody,
    visible_comments: visibleComments,
    target_message: targetMessage,
    author_type: options?.author_type ?? 'other',
    user_intent: options?.user_intent,
    additional_context: options?.additional_context,
  }
}

/**
 * Analyze a single message (comment or post) in the context of a thread.
 * Returns V2InterpretResponse-compatible shape for use in message-analyzer UI.
 */
export async function analyzeMessageInContext(
  request: ReturnType<typeof buildAnalyzeMessageInContextRequest>,
): Promise<V2InterpretResponse> {
  const { data, error } = await invokeWithAuth<V2InterpretResponse>('analyze-message-in-context', {
    method: 'POST',
    body: request,
  })
  if (error) throw new Error(error.message || 'Analysis failed')
  if (data?.error) throw new Error((data as { error: string }).error)
  return data as V2InterpretResponse
}

/**
 * Map V2InterpretResponse to the AnalysisResult shape used by AnalysisResultsPage.
 * Use when navigating from social-comment flow to /analyze/results.
 */
export function mapV2InterpretToAnalysisResult(data: V2InterpretResponse): {
  analysisEventId: string
  aMsgId: string
  sessionId?: string
  tone: string
  nuance: string
  intention: string
  toneEvidence: unknown[]
  nuanceEvidence: unknown[]
  intentionEvidence: unknown[]
  toneExplanation: string | null
  nuanceExplanation: string | null
  intentionExplanation: string | null
  summary: string | null
  warning: string | null
  recommendation: string | null
  potentialResponses: Record<string, { message: string; explanation: string }> | null
  safetyRisk: 0 | 1 | 2 | 3
  safetyTypes: string[]
  safetyEvidence: unknown[]
  safetyLabel: string
  safetyConfidence: number
  safeToRespond: boolean
  responseGuidance: V2InterpretResponse['response_guidance']
} {
  return {
    analysisEventId: data.analysis_event_id,
    aMsgId: data.analysis_event_id,
    sessionId: data.session_id,
    tone: data.tone || 'Neutral',
    nuance: data.nuance || 'None Detected',
    intention: data.intention || 'Informational',
    toneEvidence: data.tone_evidence ?? [],
    nuanceEvidence: data.nuance_evidence ?? [],
    intentionEvidence: data.intention_evidence ?? [],
    toneExplanation: data.tone_evidence_explanation ?? null,
    nuanceExplanation: data.nuance_evidence_explanation ?? null,
    intentionExplanation: data.intention_evidence_explanation ?? null,
    summary: data.summary ?? null,
    warning: data.warning ?? null,
    recommendation: data.recommendation ?? null,
    potentialResponses: data.potential_responses ?? null,
    safetyRisk: (data.safety_risk ?? 0) as 0 | 1 | 2 | 3,
    safetyTypes: data.safety_types ?? [],
    safetyEvidence: data.safety_evidence ?? [],
    safetyLabel: data.safety_label ?? 'No Risk',
    safetyConfidence: data.safety_confidence ?? 0,
    safeToRespond: data.safe_to_respond ?? true,
    responseGuidance: data.response_guidance ?? undefined,
  }
}

// ===================================
// v5-interpret: pass 1 internal analysis + pass 2 user-facing synthesis
// ===================================

/**
 * Request format for v5-interpret endpoint.
 */
export interface V5InterpretRequest {
  message: string
  sender_type: string
  relationship?: string
  perspective?: string
  context?: string | null
  additional_context?: string | null
  use_thread_context?: boolean
  thread_context?: unknown
  thread_context_messages?: string[]
}

export interface V5UnderstandingSection {
  label: string
  palette_key: string
  summary: string
  evidence: EvidenceItem[]
  /** Legacy pass-2 field — present on older rows. */
  card_title?: string
}

export interface V5ImpliedMeaningSection {
  present: boolean
  label: string
  palette_key: string
  summary: string | null
  evidence: EvidenceItem[]
  /** Legacy pass-2 field — present on older rows. */
  card_title?: string
}

export interface V5ResponseGuidance {
  should_respond: boolean
  response_type: 'none' | 'boundary' | 'refusal' | 'neutral' | 'friendly'
  reasoning: string
  suggested_actions: string[]
}

export interface V5SafetySection {
  risk_score: number
  risk_types: string[]
  risk_label: string
  confidence: number
  evidence: EvidenceItem[]
  safe_to_respond: boolean
  /** Legacy field — v5 pass 2 no longer emits this; mapper supplies defaults when absent. */
  response_guidance?: V5ResponseGuidance
}

export type V5ResponseStyles = Record<string, string>

/**
 * Response shape returned by the v5-interpret edge function.
 */
export interface V5InterpretResponse {
  analysis_event_id: string
  learning_signal_id: string | null
  session_id: string
  redacted_message: string
  pii_redaction_map?: Record<string, string>
  /** Pass 1 internal analysis — for admin/debug; not shown on results page. */
  pass1_analysis?: Record<string, unknown>
  understanding: {
    literal_meaning: V5UnderstandingSection
    implied_meaning: V5ImpliedMeaningSection
    sender_intent: V5UnderstandingSection
    safety: V5SafetySection
    overall_summary: string | null
  }
  warning: string | null
  recommendation: string | null
  /** Honest 0.0–1.0 confidence in the overall_summary / primary reading. */
  interpretation_confidence?: number
  error?: string
  code?: string
}

/**
 * Call v5-interpret: pass 1 message analysis → pass 2 user-facing synthesis.
 */
export async function analyzeMessageV5(request: V5InterpretRequest): Promise<V5InterpretResponse> {
  const { data, error } = await invokeWithAuth('v5-interpret', {
    body: {
      message: request.message,
      sender_type: request.sender_type,
      relationship: request.relationship,
      perspective: request.perspective,
      context: request.context || null,
      additional_context: request.additional_context || null,
      use_thread_context: request.use_thread_context,
      thread_context: request.thread_context,
      thread_context_messages: request.thread_context_messages,
      // Buffered (non-streaming) mode: v5-interpret returns one JSON response.
      // The progressive UI path uses analyzeMessageV5Stream instead.
      stream: false,
    },
  })

  if (error) {
    console.error('[v5-interpret] API Error:', error)
    throw new Error(`API Error: ${error.message}`)
  }

  const payloadError = readApiError(data)
  if (payloadError) {
    console.error('[v5-interpret] Backend returned error:', data)
    throw new Error(payloadError)
  }

  return data as V5InterpretResponse
}

function resolveV5CardLabel(
  section: { label?: string; card_title?: string },
  fallback = 'Neutral',
): string {
  const label = section.label?.trim()
  if (label) return label
  const legacy = section.card_title?.trim()
  if (legacy) return legacy.replace(/_/g, ' ')
  return fallback
}

/**
 * Map V5InterpretResponse to the AnalysisResult shape used by AnalysisResultsPage.
 * The `understanding` field carries the new literal/implied/sender-intent structure
 * that 2-Understand.tsx renders in place of the legacy tone/nuance/intention tabs.
 */
export function mapV5InterpretToAnalysisResult(data: V5InterpretResponse): {
  analysisEventId: string
  aMsgId: string
  sessionId?: string
  learningSignalId?: string
  tone: string
  nuance: string
  intention: string
  toneEvidence: string[] | Array<[number, number]>
  nuanceEvidence: string[] | Array<[number, number]>
  intentionEvidence: string[] | Array<[number, number]>
  toneExplanation: string | null
  nuanceExplanation: string | null
  intentionExplanation: string | null
  summary: string | null
  interpretationConfidence?: number
  warning: string | null
  recommendation: string | null
  potentialResponses: Record<string, string> | null
  redactedMessage: string
  piiRedactionMap?: Record<string, string>
  safetyRisk: 0 | 1 | 2 | 3
  safetyTypes: string[]
  safetyLabel: string
  safetyConfidence: number
  safeToRespond: boolean
  responseGuidance: V5ResponseGuidance
  understanding: {
    literalMeaning: V5UnderstandingSection
    impliedMeaning: V5ImpliedMeaningSection
    senderIntent: V5UnderstandingSection
    overallSummary: string | null
  }
} {
  const { understanding } = data

  const literalLabel = resolveV5CardLabel(understanding.literal_meaning)
  const impliedLabel = resolveV5CardLabel(understanding.implied_meaning, 'No subtext')
  const intentLabel = resolveV5CardLabel(understanding.sender_intent)

  return {
    analysisEventId: data.analysis_event_id,
    aMsgId: data.analysis_event_id,
    sessionId: data.session_id,
    learningSignalId: data.learning_signal_id ?? undefined,
    tone: literalLabel,
    nuance: impliedLabel,
    intention: intentLabel,
    toneEvidence: [],
    nuanceEvidence: [],
    intentionEvidence: [],
    toneExplanation: null,
    nuanceExplanation: null,
    intentionExplanation: null,
    summary: understanding.overall_summary ?? null,
    interpretationConfidence:
      typeof data.interpretation_confidence === 'number' &&
      Number.isFinite(data.interpretation_confidence)
        ? Math.min(1, Math.max(0, data.interpretation_confidence))
        : undefined,
    warning: data.warning ?? null,
    recommendation: data.recommendation ?? null,
    potentialResponses: null,
    redactedMessage: data.redacted_message,
    piiRedactionMap: data.pii_redaction_map,
    safetyRisk: Math.min(3, Math.max(0, understanding.safety.risk_score ?? 0)) as 0 | 1 | 2 | 3,
    safetyTypes: understanding.safety.risk_types ?? [],
    safetyLabel: understanding.safety.risk_label ?? 'No Risk',
    safetyConfidence: understanding.safety.confidence ?? 0,
    safeToRespond: understanding.safety.safe_to_respond ?? true,
    responseGuidance: understanding.safety.response_guidance ?? {
      should_respond: understanding.safety.safe_to_respond ?? true,
      response_type: 'neutral',
      reasoning: '',
      suggested_actions: [],
    },
    understanding: {
      literalMeaning: {
        label: literalLabel,
        palette_key: understanding.literal_meaning.palette_key,
        summary: understanding.literal_meaning.summary,
        evidence: understanding.literal_meaning.evidence,
      },
      impliedMeaning: {
        present: understanding.implied_meaning.present,
        label: impliedLabel,
        palette_key: understanding.implied_meaning.palette_key,
        summary: understanding.implied_meaning.summary,
        evidence: understanding.implied_meaning.evidence,
      },
      senderIntent: {
        label: intentLabel,
        palette_key: understanding.sender_intent.palette_key,
        summary: understanding.sender_intent.summary,
        evidence: understanding.sender_intent.evidence,
      },
      overallSummary: understanding.overall_summary ?? null,
    },
  }
}

/**
 * Analyze a Reddit thread for tone, safety, and community norms
 * Step 2 of two-step pattern: performs AI analysis on thread data
 *
 * @param data - Reddit URL and optional context (or pre-fetched thread data)
 * @returns Thread analysis and context data
 */
export async function analyzeSocialComment(
  data: SocialCommentRequest & { thread_data?: ThreadData },
): Promise<SocialCommentAnalysisResult> {
  let threadData: ThreadData

  // Step 1: Fetch thread if not provided
  if (data.thread_data) {
    threadData = data.thread_data
  } else {
    threadData = await fetchRedditThread(data.reddit_url)
  }

  // Step 2: Analyze thread with AI
  const { data: responseData, error } = await invokeWithAuth<SocialCommentAnalysisResult>(
    'thread-analyze',
    {
      method: 'POST',
      body: {
        thread_data: threadData,
        reddit_url: data.reddit_url,
        user_intent: data.user_intent,
        additional_context: data.additional_context,
      },
    },
  )

  if (error) {
    console.error('[analyzeSocialComment] API Error:', error)
    throw new Error(`API Error: ${error.message}`)
  }

  if (!responseData) {
    throw new Error('No response data received')
  }

  if (!responseData.success) {
    throw new Error(responseData.error || 'Analysis failed')
  }

  return responseData
}

// ===================================
// LINA Lens API
// ===================================
// All Lens HTTP requests originate from `@/lib/lens/lensApi`.
